import React, { useState, useEffect } from 'react';
import {
  adminListAuditLog,
  adminListRecycledRefs,
  adminDropRecycledRef,
  adminHardDeletePallet,
  adminForceEditPallet,
  adminHardDeleteGRN,
  adminHardDeleteChallan,
  getPallet,
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Trash2, Recycle, FileSearch, Edit3, AlertTriangle, RefreshCw, X,
} from 'lucide-react';

const ENTITY_TYPES = ['', 'pallet', 'item', 'grn', 'challan', 'user', 'customer', 'location'];
const ACTION_TYPES_HINT = [
  '', 'pallet_hard_deleted', 'pallet_force_edited', 'pallet_voided',
  'grn_hard_deleted', 'challan_hard_deleted', 'item_hard_deleted',
  'pallet_placed', 'location_move_confirmed', 'issue_full', 'issue_partial',
  'qc_hold_placed', 'qc_hold_cleared', 'user_invited',
];

function ConfirmDelete({ open, onOpenChange, title, description, onConfirm, requireText = 'DELETE' }) {
  const [typed, setTyped] = useState('');
  useEffect(() => { if (!open) setTyped(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle size={18} /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-gray-600">{description}</div>
          <div>
            <Label className="text-xs">
              Type <span className="font-mono font-bold">{requireText}</span> to confirm
            </Label>
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="h-9 text-sm font-mono"
              data-testid="confirm-delete-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={typed !== requireText}
            onClick={() => { onConfirm(); onOpenChange(false); }}
            className="bg-red-600 hover:bg-red-700"
            data-testid="confirm-delete-btn"
          >
            <Trash2 size={14} className="mr-1.5" /> Permanently Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDataTab() {
  // Recycled pool
  const [pool, setPool] = useState({ pallet_refs: [], challan_refs: [], pallet_count: 0, challan_count: 0 });

  // Audit log
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [filterEntity, setFilterEntity] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterRef, setFilterRef] = useState('');
  const [auditLoading, setAuditLoading] = useState(false);
  const [logDetail, setLogDetail] = useState(null);

  // Quick delete
  const [palletInput, setPalletInput] = useState('');
  const [grnInput, setGrnInput] = useState('');
  const [challanInput, setChallanInput] = useState('');
  const [confirmTarget, setConfirmTarget] = useState(null); // {type, ref}

  // Force-edit
  const [editPalletRef, setEditPalletRef] = useState('');
  const [editPallet, setEditPallet] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    loadPool();
    loadAudit();
  }, []);

  const loadPool = async () => {
    try {
      const res = await adminListRecycledRefs();
      setPool(res.data);
    } catch { /* silent */ }
  };

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const params = { limit: 100 };
      if (filterEntity) params.entity_type = filterEntity;
      if (filterAction) params.action_type = filterAction;
      if (filterRef) params.entity_ref = filterRef;
      const res = await adminListAuditLog(params);
      setAuditLogs(res.data.logs || []);
      setAuditTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load audit log');
    }
    setAuditLoading(false);
  };

  const handleDropRecycled = async (counter, ref) => {
    try {
      await adminDropRecycledRef({ counter, ref });
      toast.success(`Removed ${ref} from pool`);
      loadPool();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const requestDelete = (type, ref) => {
    if (!ref.trim()) {
      toast.error('Enter a reference');
      return;
    }
    setConfirmTarget({ type, ref: ref.trim() });
  };

  const performDelete = async () => {
    const { type, ref } = confirmTarget;
    try {
      let res;
      if (type === 'pallet') res = await adminHardDeletePallet(ref);
      else if (type === 'grn') res = await adminHardDeleteGRN(ref);
      else if (type === 'challan') res = await adminHardDeleteChallan(ref);
      const d = res.data;
      if (d.recycled) {
        toast.success(`Deleted ${ref}. Ref recycled — available for reuse.`);
      } else {
        toast.success(`Deleted ${ref}.`);
      }
      if (type === 'pallet') setPalletInput('');
      if (type === 'grn') setGrnInput('');
      if (type === 'challan') setChallanInput('');
      loadPool();
      loadAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const loadPalletForEdit = async () => {
    if (!editPalletRef.trim()) return;
    setEditLoading(true);
    try {
      const res = await getPallet(editPalletRef.trim());
      setEditPallet(res.data);
      setEditForm({
        status: res.data.status || '',
        slot_address: res.data.slot_address || '',
        location_type: res.data.location_type || '',
        notes: res.data.notes || '',
        pallet_capacity: res.data.pallet_capacity ?? '',
        date_placed: res.data.date_placed || '',
        date_issued: res.data.date_issued || '',
        qc_hold_reason: res.data.qc_hold_reason || '',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Pallet not found');
      setEditPallet(null);
    }
    setEditLoading(false);
  };

  const submitForceEdit = async () => {
    const payload = {};
    Object.keys(editForm).forEach((k) => {
      const v = editForm[k];
      if (v === '' || v === null || v === undefined) return;
      payload[k] = k === 'pallet_capacity' ? parseInt(v, 10) : v;
    });
    if (Object.keys(payload).length === 0) {
      toast.error('No changes to save');
      return;
    }
    setEditLoading(true);
    try {
      await adminForceEditPallet(editPallet.id, payload);
      toast.success(`${editPallet.pallet_ref} updated (force-edit)`);
      setEditPallet(null);
      setEditPalletRef('');
      loadAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
    setEditLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-data-tab">
      {/* Warning banner */}
      <Card className="border-0 shadow-sm bg-amber-50">
        <CardContent className="p-3 flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <div className="font-semibold mb-0.5">Admin Data Management — irreversible operations</div>
            All actions are logged to audit. Hard-deleting a pallet or challan releases its reference number
            (e.g. <span className="font-mono">SFX-0042</span>) for reuse on the next entry.
          </div>
        </CardContent>
      </Card>

      {/* Recycled refs pool */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Recycle size={16} className="text-[#0e7a45]" /> Recycled Reference Pool
            <Badge variant="outline" className="ml-auto text-[10px]">
              {pool.pallet_count} pallet · {pool.challan_count} challan
            </Badge>
            <Button size="sm" variant="ghost" onClick={loadPool} className="h-7 px-2" data-testid="reload-pool">
              <RefreshCw size={12} />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <div className="text-xs text-gray-500 mb-1">Pallet refs ready for reuse (FIFO):</div>
            <div className="flex flex-wrap gap-1.5">
              {pool.pallet_refs.length === 0 ? (
                <span className="text-xs text-gray-400">No recycled pallet refs</span>
              ) : pool.pallet_refs.map((r) => (
                <Badge key={r.id} variant="outline" className="font-mono text-[11px] gap-1.5 pr-1" data-testid={`pool-pallet-${r.ref}`}>
                  {r.ref}
                  <button
                    onClick={() => handleDropRecycled('pallet_ref', r.ref)}
                    className="hover:text-red-600"
                    title="Drop from pool (do not reuse)"
                  >
                    <X size={11} />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Challan refs ready for reuse:</div>
            <div className="flex flex-wrap gap-1.5">
              {pool.challan_refs.length === 0 ? (
                <span className="text-xs text-gray-400">No recycled challan refs</span>
              ) : pool.challan_refs.map((r) => (
                <Badge key={r.id} variant="outline" className="font-mono text-[11px] gap-1.5 pr-1" data-testid={`pool-challan-${r.ref}`}>
                  {r.ref}
                  <button
                    onClick={() => handleDropRecycled('challan_ref', r.ref)}
                    className="hover:text-red-600"
                  >
                    <X size={11} />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hard delete by ref */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Trash2 size={16} className="text-red-600" /> Hard-Delete by Reference
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Pallet (e.g. SFX-0042)</Label>
            <div className="flex gap-1.5 mt-1">
              <Input
                value={palletInput}
                onChange={(e) => setPalletInput(e.target.value.toUpperCase())}
                placeholder="SFX-XXXX"
                className="h-9 text-sm font-mono"
                data-testid="delete-pallet-input"
              />
              <Button
                variant="outline"
                onClick={() => requestDelete('pallet', palletInput)}
                className="h-9 border-red-300 text-red-600"
                data-testid="delete-pallet-btn"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs">GRN (e.g. GRN-20260504-001)</Label>
            <div className="flex gap-1.5 mt-1">
              <Input
                value={grnInput}
                onChange={(e) => setGrnInput(e.target.value.toUpperCase())}
                placeholder="GRN-YYYYMMDD-NNN"
                className="h-9 text-sm font-mono"
                data-testid="delete-grn-input"
              />
              <Button
                variant="outline"
                onClick={() => requestDelete('grn', grnInput)}
                className="h-9 border-red-300 text-red-600"
                data-testid="delete-grn-btn"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Challan (e.g. DC-00012)</Label>
            <div className="flex gap-1.5 mt-1">
              <Input
                value={challanInput}
                onChange={(e) => setChallanInput(e.target.value.toUpperCase())}
                placeholder="DC-XXXXX"
                className="h-9 text-sm font-mono"
                data-testid="delete-challan-input"
              />
              <Button
                variant="outline"
                onClick={() => requestDelete('challan', challanInput)}
                className="h-9 border-red-300 text-red-600"
                data-testid="delete-challan-btn"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Force-edit pallet */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Edit3 size={16} className="text-[#1a2fce]" /> Force-Edit Pallet
            <span className="ml-2 text-[10px] text-gray-400 font-normal">All fields except pallet_ref</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={editPalletRef}
              onChange={(e) => setEditPalletRef(e.target.value.toUpperCase())}
              placeholder="Enter SFX-XXXX or pallet UUID"
              className="h-9 text-sm font-mono"
              data-testid="edit-pallet-ref-input"
            />
            <Button
              onClick={loadPalletForEdit}
              disabled={editLoading || !editPalletRef.trim()}
              className="h-9"
              style={{ backgroundColor: '#1a2fce' }}
              data-testid="load-pallet-edit-btn"
            >
              Load
            </Button>
          </div>
          {editPallet && (
            <div className="border rounded-lg p-3 space-y-2 bg-gray-50">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono font-bold text-[#0d1f6e]">{editPallet.pallet_ref}</span>
                <Badge variant="outline" className="text-[10px]">{editPallet.status}</Badge>
                <Button size="sm" variant="ghost" onClick={() => setEditPallet(null)} className="ml-auto h-7 px-2">
                  <X size={12} />
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div>
                  <Label className="text-[10px]">Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm((p) => ({ ...p, status: v }))}>
                    <SelectTrigger className="h-8 text-xs" data-testid="edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['packing', 'ready', 'placed', 'partial', 'qc_hold', 'issued', 'void'].map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Slot Address</Label>
                  <Input
                    value={editForm.slot_address || ''}
                    onChange={(e) => setEditForm((p) => ({ ...p, slot_address: e.target.value.toUpperCase() }))}
                    placeholder="e.g. B03-L2-P1 or empty"
                    className="h-8 text-xs font-mono"
                    data-testid="edit-slot"
                  />
                </div>
                <div>
                  <Label className="text-[10px]">Location Type</Label>
                  <Select value={editForm.location_type} onValueChange={(v) => setEditForm((p) => ({ ...p, location_type: v }))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rack">rack</SelectItem>
                      <SelectItem value="floor">floor</SelectItem>
                      <SelectItem value="staging">staging</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Capacity</Label>
                  <Input
                    type="number"
                    value={editForm.pallet_capacity}
                    onChange={(e) => setEditForm((p) => ({ ...p, pallet_capacity: e.target.value }))}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px]">Notes</Label>
                  <Input
                    value={editForm.notes || ''}
                    onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-[10px]">Date Placed (ISO)</Label>
                  <Input
                    value={editForm.date_placed || ''}
                    onChange={(e) => setEditForm((p) => ({ ...p, date_placed: e.target.value }))}
                    className="h-8 text-xs font-mono"
                  />
                </div>
                <div>
                  <Label className="text-[10px]">Date Issued (ISO)</Label>
                  <Input
                    value={editForm.date_issued || ''}
                    onChange={(e) => setEditForm((p) => ({ ...p, date_issued: e.target.value }))}
                    className="h-8 text-xs font-mono"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px]">QC Hold Reason</Label>
                  <Input
                    value={editForm.qc_hold_reason || ''}
                    onChange={(e) => setEditForm((p) => ({ ...p, qc_hold_reason: e.target.value }))}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
              <Button
                onClick={submitForceEdit}
                disabled={editLoading}
                className="w-full h-9 mt-2"
                style={{ backgroundColor: '#1a2fce' }}
                data-testid="submit-force-edit"
              >
                Save Force-Edit (logged to audit)
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileSearch size={16} className="text-[#1a2fce]" /> Audit Log Review
            <Badge variant="outline" className="ml-2 text-[10px]">{auditTotal} total</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-[10px]">Entity</Label>
              <Select value={filterEntity} onValueChange={(v) => setFilterEntity(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-entity"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  {ENTITY_TYPES.filter(Boolean).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px]">Action</Label>
              <Select value={filterAction} onValueChange={(v) => setFilterAction(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-action"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  {ACTION_TYPES_HINT.filter(Boolean).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px]">Entity Ref (exact)</Label>
              <div className="flex gap-1">
                <Input
                  value={filterRef}
                  onChange={(e) => setFilterRef(e.target.value)}
                  placeholder="e.g. SFX-0042"
                  className="h-8 text-xs font-mono"
                  data-testid="filter-ref"
                />
                <Button onClick={loadAudit} disabled={auditLoading} className="h-8 text-xs" style={{ backgroundColor: '#1a2fce' }} data-testid="apply-audit-filter">
                  <RefreshCw size={12} className={auditLoading ? 'animate-spin' : ''} />
                </Button>
              </div>
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-[10px] uppercase text-gray-500">
                    <th className="px-2 py-1.5">Time</th>
                    <th className="px-2 py-1.5">Action</th>
                    <th className="px-2 py-1.5">Entity</th>
                    <th className="px-2 py-1.5">User</th>
                    <th className="px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.length === 0 ? (
                    <tr><td colSpan={5} className="text-center text-gray-400 py-4">No entries</td></tr>
                  ) : auditLogs.map((log) => (
                    <tr key={log.id} className="border-t hover:bg-gray-50" data-testid={`audit-row-${log.id}`}>
                      <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={
                          log.action_type?.includes('hard_deleted') ? 'text-red-600 font-semibold' :
                          log.action_type?.includes('force_edited') ? 'text-amber-700 font-semibold' :
                          'text-gray-700'
                        }>
                          {log.action_type}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 font-mono">
                        {log.entity_type}{log.entity_ref ? ' · ' + log.entity_ref : ''}
                      </td>
                      <td className="px-2 py-1.5">{log.user_email || log.user_name || '—'}</td>
                      <td className="px-2 py-1.5">
                        <Button size="sm" variant="ghost" onClick={() => setLogDetail(log)} className="h-6 px-2 text-[10px]">
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!logDetail} onOpenChange={(o) => !o && setLogDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-sm">Audit Log Entry</DialogTitle></DialogHeader>
          {logDetail && (
            <div className="space-y-2 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-500">Time: </span>{new Date(logDetail.timestamp).toLocaleString()}</div>
                <div><span className="text-gray-500">Action: </span><span className="font-semibold">{logDetail.action_type}</span></div>
                <div><span className="text-gray-500">User: </span>{logDetail.user_email}</div>
                <div><span className="text-gray-500">Role: </span>{logDetail.user_role}</div>
                <div><span className="text-gray-500">Entity: </span><span className="font-mono">{logDetail.entity_type} / {logDetail.entity_ref || logDetail.entity_id}</span></div>
                <div><span className="text-gray-500">IP: </span>{logDetail.ip_address || '—'}</div>
              </div>
              {logDetail.notes && (
                <div className="bg-gray-50 p-2 rounded border text-gray-700">{logDetail.notes}</div>
              )}
              {logDetail.before_state && (
                <div>
                  <div className="text-[10px] uppercase text-gray-500 mt-2 mb-1">Before</div>
                  <pre className="bg-amber-50 p-2 rounded text-[10px] overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(logDetail.before_state, null, 2)}
                  </pre>
                </div>
              )}
              {logDetail.after_state && (
                <div>
                  <div className="text-[10px] uppercase text-gray-500 mt-2 mb-1">After</div>
                  <pre className="bg-green-50 p-2 rounded text-[10px] overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(logDetail.after_state, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDelete
        open={!!confirmTarget}
        onOpenChange={(o) => !o && setConfirmTarget(null)}
        title={`Hard-delete ${confirmTarget?.type} ${confirmTarget?.ref}?`}
        description={
          <>
            This permanently removes the {confirmTarget?.type} and all related items, boxes and transactions.
            {(confirmTarget?.type === 'pallet' || confirmTarget?.type === 'challan') && (
              <> The reference <span className="font-mono font-bold">{confirmTarget?.ref}</span> will be recycled and used for the next entry.</>
            )}
          </>
        }
        onConfirm={performDelete}
      />
    </div>
  );
}
